/**
 * Biblioteca da sala (FC.Room.UIModule.RoomLibrary) e sincronização de fichas
 * (NDB). Os esquemas espelham os tipos de `@rpgdesk/ndb` para validar o que chega
 * pela rede.
 */
import { z } from 'zod';
import { IdSchema } from './domain.js';

/* ------------------------------------------------------------------ biblioteca */

/**
 * `scene`: mapa da mesa (TfrmSceneModule), também guardado como documento NDB.
 * `dock`: tabela ativa no TablesDock da sala (form de plugin do tipo `tablesDock`),
 * uma por tipo de dado; não aparece na árvore da biblioteca.
 * `macros`: as macros da mesa (para todos), uma por sala; também fora da árvore.
 */
export const LibraryItemKindSchema = z.enum(['folder', 'sheet', 'scene', 'dock', 'macros']);
export type LibraryItemKind = z.infer<typeof LibraryItemKindSchema>;

export const LibraryItemSchema = z.object({
  id: IdSchema,
  roomId: IdSchema,
  /** pasta onde está (null = raiz da biblioteca) */
  parentId: IdSchema.nullable(),
  kind: LibraryItemKindSchema,
  name: z.string().min(1).max(80),
  /** tipo de dado do plugin que edita a ficha (ex.: br.com.rrpg.DnD5_S3) */
  dataType: z.string().max(120).nullable(),
  /** dono (quem pode editar a ficha além dos mestres) */
  ownerId: IdSchema.nullable(),
  position: z.number().int(),
  /** tabela (dock) desativada: some da sala, mas os dados ficam guardados para reativar */
  inactive: z.boolean().default(false),
  /**
   * visível para quem não é mestre (o `Visivel` do TBibliotecaItem do original). Pastas, fichas e
   * cenas nascem ocultas, e o mestre as libera (BRAZ-16); ver `canSeeLibraryItem`
   */
  visible: z.boolean().default(true),
  createdAt: z.number().int().nonnegative(),
});
export type LibraryItem = z.infer<typeof LibraryItemSchema>;

/** Quem olha a biblioteca: mestres veem tudo. */
export type LibraryViewer = { userId: string; gm: boolean };

/**
 * Visível na hierarquia: o item e todas as pastas acima dele são visíveis (o
 * `GetVisivelHierarquicamente` do original). Liberar uma pasta não libera o que está dentro, e
 * uma pasta oculta esconde tudo o que está dentro.
 */
export function visibleInHierarchy(all: readonly LibraryItem[], item: LibraryItem): boolean {
  const byId = new Map(all.map((i) => [i.id, i]));
  const seen = new Set<string>();
  for (let at: LibraryItem | undefined = item; at && !seen.has(at.id); at = at.parentId ? byId.get(at.parentId) : undefined) {
    if (!at.visible) return false;
    seen.add(at.id);
  }
  return true;
}

/**
 * Se a pessoa vê o item da biblioteca (BRAZ-16): mestres veem tudo; os outros, o que é visível
 * na hierarquia, e sempre as fichas de que são donos. Tabelas da sala e macros são da mesa toda.
 */
export function canSeeLibraryItem(all: readonly LibraryItem[], item: LibraryItem, viewer: LibraryViewer): boolean {
  if (viewer.gm || item.kind === 'dock' || item.kind === 'macros') return true;
  if (item.kind === 'sheet' && item.ownerId === viewer.userId) return true;
  return visibleInHierarchy(all, item);
}

/**
 * A biblioteca como a pessoa a vê: só os itens que ela vê. A ficha própria dentro de uma pasta
 * que a pessoa não vê fica na pasta visível mais próxima acima dela, ou na raiz: a pasta oculta
 * não aparece, nem pelo id.
 */
export function libraryViewOf(all: readonly LibraryItem[], viewer: LibraryViewer): LibraryItem[] {
  if (viewer.gm) return [...all];
  const seen = new Set(all.filter((item) => canSeeLibraryItem(all, item, viewer)).map((item) => item.id));
  const byId = new Map(all.map((i) => [i.id, i]));
  return all
    .filter((item) => seen.has(item.id))
    .map((item) => {
      let parentId = item.parentId;
      const walked = new Set<string>();
      while (parentId && !seen.has(parentId) && !walked.has(parentId)) {
        walked.add(parentId);
        parentId = byId.get(parentId)?.parentId ?? null;
      }
      return parentId === item.parentId ? item : { ...item, parentId: parentId && seen.has(parentId) ? parentId : null };
    });
}

/* ------------------------------------------------------------------ plugins da mesa */

export const WebhookDeclSchema = z.object({
  name: z.string().min(1).max(40),
  description: z.string().max(300),
  example: z.unknown().optional(),
});
export type WebhookDecl = z.infer<typeof WebhookDeclSchema>;

/**
 * Plugin web instalado na mesa (docs/spec/web-plugins.md): HTML, CSS e JS num iframe, falando com o
 * RPG Desk pelo rpgdesk.js. O pacote fica no Desk Cloud de quem instalou (o `fileId`) ou, nos plugins
 * oficiais, no servidor (o `fileId` é o id do oficial), e o servidor o entrega pronto.
 */
export const RoomPluginSchema = z.object({
  roomId: IdSchema,
  fileId: IdSchema,
  moduleId: z.string().min(1).max(120),
  name: z.string().min(1).max(120),
  version: z.string().max(40),
  /** o pacote como o servidor o entrega */
  web: z
    .object({
      sha256: z.string().regex(/^[0-9a-f]{64}$/),
      /** pasta do pacote, com barra no fim: endereço completo, ou caminho do servidor (`/p/<sha256>/`) */
      baseUrl: z.string().max(500),
      author: z.string().max(120).optional(),
      description: z.string().max(1000).optional(),
      webhooks: z.array(WebhookDeclSchema).max(20),
      /** o nome em cada língua, quando o manifesto traz `{ "pt": …, "en": … }` (o `name` é o em português) */
      names: z.record(z.string(), z.string().max(120)).optional(),
      /** a descrição em cada língua, quando o manifesto traz `{ "pt": …, "en": … }` (a `description` é a em português) */
      descriptions: z.record(z.string(), z.string().max(1000)).optional(),
      /** o título de cada tipo de dado em cada língua, pelo id do tipo */
      titles: z.record(z.string(), z.record(z.string(), z.string().max(120))).optional(),
      /** o ícone do manifesto (PNG ou SVG), caminho dentro do pacote */
      icon: z.string().max(300).optional(),
      /** instalado do catálogo de plugins oficiais: o id do oficial (o `fileId` também é ele) */
      officialId: IdSchema.optional(),
    }),
  dataTypes: z
    .array(
      z.object({
        id: z.string().min(1).max(120),
        title: z.string().max(120),
        formName: z.string().max(120),
        /** `sheetTemplate` (ficha da biblioteca) ou `tablesDock` (tabela da sala); sem valor = ficha */
        formType: z.string().max(40).optional(),
        /** o HTML da ficha ou tabela, dentro do pacote */
        entry: z.string().max(300).optional(),
        /** partes da ficha só do mestre (`gm`) ou do dono e dos mestres (`owner`) */
        private: z.record(z.string().max(200), z.enum(['gm', 'owner'])).optional(),
      }),
    )
    .max(50),
  installedBy: IdSchema,
  installedAt: z.number().int().nonnegative(),
});
export type RoomPlugin = z.infer<typeof RoomPluginSchema>;

/* ------------------------------------------------------------------ NDB */

const NodeIdSchema = z.string().min(1).max(80);
const AttrValueSchema = z.union([z.string().max(100_000), z.number().finite(), z.boolean()]);
const AllowanceSchema = z.enum(['allow', 'deny', 'strongAllow']);
const PermissionSchema = z.string().min(1).max(40);

export const NdbOpSchema = z.discriminatedUnion('t', [
  z.object({ t: z.literal('set'), node: NodeIdSchema, name: z.string().min(1).max(256), value: AttrValueSchema.nullable() }),
  z.object({ t: z.literal('add'), parent: NodeIdSchema, id: NodeIdSchema, name: z.string().min(1).max(256), index: z.number().int().min(0).optional() }),
  z.object({ t: z.literal('del'), node: NodeIdSchema }),
  z.object({ t: z.literal('clear'), node: NodeIdSchema }),
  z.object({ t: z.literal('rmChildren'), node: NodeIdSchema, name: z.string().min(1).max(256) }),
  z.object({ t: z.literal('move'), node: NodeIdSchema, index: z.number().int().min(0).max(1_000_000) }),
  z.object({
    t: z.literal('perm'),
    node: NodeIdSchema,
    kind: z.enum(['user', 'group']),
    id: z.string().min(1).max(80),
    permission: PermissionSchema,
    allowance: AllowanceSchema.nullable(),
  }),
  z.object({ t: z.literal('resetPerms'), node: NodeIdSchema }),
]);

export const NdbBatchSchema = z.object({
  origin: z.string().min(1).max(80),
  clientSeq: z.number().int().positive(),
  ops: z.array(NdbOpSchema).min(1).max(2000),
});

export const NdbSequencedBatchSchema = NdbBatchSchema.extend({ seq: z.number().int().positive() });

const PermissionEntrySchema = z.object({ kind: z.enum(['user', 'group']), id: z.string(), permission: z.string(), allowance: AllowanceSchema });

export type NdbSnapshotShape = {
  id: string;
  name: string;
  attrs: Record<string, string | number | boolean>;
  perms: z.infer<typeof PermissionEntrySchema>[];
  children: NdbSnapshotShape[];
};

export const NdbSnapshotSchema: z.ZodType<NdbSnapshotShape> = z.lazy(() =>
  z.object({
    id: NodeIdSchema,
    name: z.string(),
    attrs: z.record(z.string(), AttrValueSchema),
    perms: z.array(PermissionEntrySchema),
    children: z.array(NdbSnapshotSchema),
  }),
);

/** Grupos de permissão que o servidor atribui a quem está na sala. */
export const NDB_GROUPS = {
  /** todo mundo que está na sala */
  everyone: 'everyone',
  gms: 'gms',
  players: 'players',
  spectators: 'spectators',
} as const;
